import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import Service from '@/App/Service/Entity/Service'
import Integration from '@/App/Integration/Entity/Integration'
import IntegrationID from '@/App/Integration/Entity/IntegrationID'
import ServicePrice from '@/App/Service/Entity/ServicePrice'
import CustomField from '@/App/CustomField/Entity/CustomField'
import Availability from '@/App/Availability/Entity/Availability'
import AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
import type EntityWithAssociatedAvailabilityInterface from '@/App/Availability/Entity/EntityWithAssociatedAvailabilityInterface'
import {
    MeetingLocationTypeEnum,
    type MeetingLocationType,
    type ServiceAggregateData,
} from '@/Shared/AutoGenerated/Main'
import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'

export default class ServiceAggregate implements EntityWithAssociatedAvailabilityInterface {
    constructor(
        public readonly service: Service,
        public readonly servicePrices: ServicePrice[],
        public readonly customFields: CustomField[],
        public readonly integration: Integration | null,
        public readonly overlappingAvailability: Availability | null,
        public readonly enabledMeetingLocations: MeetingLocationType[],
    ) {
        ensureNoUndefinedProperties(this)
    }

    getServicePriceById(servicePriceId: number): ServicePrice {
        const servicePrice = this.servicePrices.find(
            (servicePrice) => servicePrice.id.value === servicePriceId,
        )

        if (!servicePrice) {
            throw new Error(`Service price of ID '${servicePriceId}' not found`)
        }

        return servicePrice
    }

    getIntegrationId(): IntegrationID | null {
        return this.integration?.id ?? null
    }

    getDefaultServicePrice(): ServicePrice | null {
        return this.servicePrices[0] ?? null
    }

    isPaymentRequired(): boolean {
        return this.service.isPaymentRequired
    }

    isPaymentNotRequired(): boolean {
        return this.isPaymentRequired() === false
    }

    shouldRenderPaymentSection(): boolean {
        return this.isPaymentRequired()
    }

    shouldRenderCustomFieldsSection(): boolean {
        return this.customFields.length > 0
    }

    shouldRenderMeetingDetailsSectionTitle(): boolean {
        return (
            this.shouldRenderCustomFieldsSection() ||
            this.shouldRenderPaymentSection()
        )
    }

    shouldNotRenderMeetingDetailsSectionTitle(): boolean {
        return !this.shouldRenderMeetingDetailsSectionTitle()
    }

    getUniqueEnabledMeetingLocationTypes(): MeetingLocationTypeEnum[] {
        const types = this.enabledMeetingLocations.map(
            (meetingLocation: MeetingLocationType) => {
                return meetingLocation.type
            },
        )

        let uniqueValues = [...new Set(types)]

        // Edge case: if "InPersonAttendeeDefined" and "InPersonHostDefined" are included at the same time, remove one to avoid displaying the same icon twice
        if (
            uniqueValues.includes(
                MeetingLocationTypeEnum.InPersonAttendeeDefined,
            ) &&
            uniqueValues.includes(MeetingLocationTypeEnum.InPersonHostDefined)
        ) {
            return uniqueValues.filter(
                (meetingLocationType: MeetingLocationTypeEnum) => {
                    return (
                        meetingLocationType !==
                        MeetingLocationTypeEnum.InPersonAttendeeDefined
                    )
                },
            )
        }

        return uniqueValues
    }

    isMeetingLocationEnabled(meetingLocationId: string): boolean {
        return !!this.enabledMeetingLocations.find(
            (enabledMeetingLocation: MeetingLocationType) => {
                return enabledMeetingLocation.id === meetingLocationId
            },
        )
    }

    getMeetingLocationByIdOrNull(
        id: string | null,
    ): MeetingLocationType | null {
        return (
            this.enabledMeetingLocations.find(
                (enabledMeetingLocation: MeetingLocationType) => {
                    return enabledMeetingLocation.id === id
                },
            ) ?? null
        )
    }

    getMeetingLocationById(id: string): MeetingLocationType {
        const meetingLocation = this.getMeetingLocationByIdOrNull(id)

        if (!meetingLocation) {
            throw new Error(`Missing MeetingLocation for ID ${id}`)
        }

        return meetingLocation
    }

    getPreSelectedMeetingLocation(): MeetingLocationType | null {
        return this.enabledMeetingLocations.length === 1
            ? this.enabledMeetingLocations[0]
            : null
    }

    // ------------------------
    // EntityWithAssociatedAvailabilityInterface implementation
    // ------------------------

    getCurrentlyAssociatedAvailabilityId(): AvailabilityID | null {
        return this.overlappingAvailability?.id ?? null
    }

    getSectionTitle(): string {
        return 'Overlapping Availability'
    }

    getRequestBodyFieldName(): RequestBodyKeyEnum {
        return RequestBodyKeyEnum.App_UpdateService_OverlappingAvailabilityId
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: ServiceAggregateData): ServiceAggregate {
        return new ServiceAggregate(
            Service.hydrateSingle(entityData.service),
            ServicePrice.hydrateMultiple(entityData.servicePrices),
            CustomField.hydrateMultiple(entityData.customFields),
            Integration.hydrateSingleOrNull(entityData.integration),
            Availability.hydrateSingleOrNull(
                entityData.overlappingAvailability,
            ),
            entityData.enabledMeetingLocations,
        )
    }
}
